import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyableReference } from "@/components/ui/copyable-reference";
import { Checkbox } from "@/components/ui/form-controls";
import { Field, Input } from "@/components/ui/input";
import { SSO_LOCALES, formatTemplate, useSsoLocale } from "@/i18n/use-sso-locale";
import { isSafeReturnPath } from "@/lib/step-up-resume";
import { cn } from "@/lib/utils";

/**
 * "Sign in with SSO" panel on the unauthenticated login page (ALL-5464,
 * SPEC-SSO-7 §1.1).
 *
 * The form is a real top-level `<form method="post" action="/auth/sso/start">`,
 * deliberately not `fetch`: the gateway answers POST /auth/sso/start with a 303
 * to the organization's IdP (or back here with `?sso=`), and only a native form
 * submission lets that redirect drive the browser — an XHR would leave the
 * navigation to us and would also mean holding the work email in JS memory for
 * a response the gateway never returns a body for. See `.sso-inputs/
 * sso-contract.md` "POST /auth/sso/start".
 *
 * Non-enumeration is the design constraint, not a nicety:
 *   - the gateway's answer is uniform (`?sso=unavailable&ref=…`) whether the
 *     domain is unregistered, the IdP is misconfigured, or the email does not
 *     exist; this panel renders one generic message for all of them and never
 *     says "no such domain"/"not registered";
 *   - nothing here calls a lookup endpoint while the user types — client-side
 *     validation is one `@` and a non-empty domain, so keystrokes cannot be
 *     turned into an oracle;
 *   - the `ref` is rendered only when it matches `REFERENCE_PATTERN`, because a
 *     redirect query string is attacker-controllable text that would otherwise
 *     render verbatim on the login page (the same bound the error page applies,
 *     D636). The reference is a support handle, never a reason: it carries no
 *     tenant or IdP identifier.
 *
 * Keys are namespaced `login.sso.` in the shared `src/i18n/sso-locales/`
 * catalogues (D150: every language ships in the same change), so the admin
 * surface's `sso.` keys and this panel's share one catalogue without collision.
 */

/** Where the gateway accepts the SSO start form (frozen contract). */
export const SSO_START_ACTION = "/auth/sso/start";

/** SPEC-SSO-7 §1.1.4: the remembered organization sign-in, domain only. */
export const SSO_REMEMBERED_DOMAIN_KEY = "alloy.sso.domain";

/** SPEC-SSO-7 §1.2: Continue stays disabled for this long after a 303. */
export const SSO_RATE_LIMIT_SECONDS = 60;

/** A support reference is `[A-Za-z0-9._:/=@+-]`, bounded. Anything else is not
 * rendered at all rather than escaped-and-shown, so a crafted `ref` cannot put
 * arbitrary text on the sign-in page. */
const REFERENCE_PATTERN = /^[A-Za-z0-9._:/=@+-]{1,256}$/;

/** A work-email domain, lower-cased, punycode allowed, at least two labels. */
const DOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

export type SsoQueryState = "unavailable" | "rate_limited" | "required";

export interface SsoQueryParams {
  /** The state the gateway sent us back with, or null for a normal visit. */
  state: SsoQueryState | null;
  /** The support reference, only when it is safe to render. */
  reference: string | null;
  /** SPEC-SSO-7 §1.2 `?sso=required&domain=D`: the domain to pre-fill. */
  domain: string | null;
}

/**
 * Reads one query parameter without form-decoding it.
 *
 * `URLSearchParams` parses the query as `application/x-www-form-urlencoded`,
 * where `+` means a space. A support reference (D636) is an opaque server-issued
 * handle and may legitimately contain `+` — decoding it as a space would both
 * drop a real reference (`abc/def+ghi=jkl@mno`) and let one character in the
 * query string silently rewrite the value the page renders. `%xx` escapes are
 * still decoded, so a percent-encoded payload is rejected by the pattern below
 * on its decoded form, never smuggled through as escapes.
 */
function rawQueryParam(search: string, name: string): string | null {
  const query = search.startsWith("?") ? search.slice(1) : search;
  for (const pair of query.split("&")) {
    const separator = pair.indexOf("=");
    const encodedKey = separator === -1 ? pair : pair.slice(0, separator);
    let key = encodedKey;
    try {
      key = decodeURIComponent(encodedKey);
    } catch {
      /* An undecodable escape is not this parameter's name. */
    }
    if (key !== name) continue;
    const raw = separator === -1 ? "" : pair.slice(separator + 1);
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

function boundedDomain(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const lower = value.trim().toLowerCase();
  return lower.length <= 253 && DOMAIN_PATTERN.test(lower) ? lower : null;
}

/** Reads `?sso=`, `?ref=` and `?domain=` off the login URL. Unknown states are
 * ignored rather than rendered, so a hand-crafted `?sso=whatever` shows the
 * plain login card instead of an empty alert. */
export function ssoParamsFrom(search: string): SsoQueryParams {
  const params = new URLSearchParams(search);
  const rawState = params.get("sso");
  const state: SsoQueryState | null =
    rawState === "unavailable" || rawState === "rate_limited" || rawState === "required" ? rawState : null;
  // Read raw: a `+` in a reference is a `+`, not a space (see rawQueryParam).
  const rawRef = rawQueryParam(search, "ref");
  return {
    state,
    reference: rawRef !== null && REFERENCE_PATTERN.test(rawRef) ? rawRef : null,
    domain: state === "required" ? boundedDomain(params.get("domain")) : null,
  };
}

/** The same safe return path the Google button uses: whatever `return_to` the
 * method's start URL already carries, re-validated with the repo's stricter
 * same-origin check (`isSafeReturnPath`) and defaulted to `/`. A value that
 * fails is dropped, never forwarded. */
export function returnToFromStartUrl(startUrl: string | undefined): string {
  if (!startUrl) return "/";
  const query = startUrl.includes("?") ? startUrl.slice(startUrl.indexOf("?") + 1) : "";
  const candidate = new URLSearchParams(query).get("return_to");
  return isSafeReturnPath(candidate) ? candidate : "/";
}

/** The domain part of a work email, lower-cased, or null. An address with no
 * local part (`@acme.com`, the remembered-domain pre-fill) still has a domain:
 * that is exactly what the checkbox stores. */
export function domainFromEmail(email: string): string | null {
  const at = email.lastIndexOf("@");
  return at === -1 ? null : boundedDomain(email.slice(at + 1));
}

/** SPEC-SSO-7 §1.1.3: client-side validation checks only for one `@` and a
 * non-empty domain. Returns true when the field should block submission. */
export function isIncompleteWorkEmail(email: string): boolean {
  const trimmed = email.trim();
  const at = trimmed.indexOf("@");
  if (at <= 0 || at !== trimmed.lastIndexOf("@")) return true;
  return domainFromEmail(trimmed) === null;
}

/** localStorage refuses to exist in some privacy modes; a remembered domain is
 * a convenience, so every access is guarded and a failure is a no-op. */
export function readRememberedDomain(): string | null {
  try {
    return boundedDomain(window.localStorage.getItem(SSO_REMEMBERED_DOMAIN_KEY));
  } catch {
    return null;
  }
}

function writeRememberedDomain(domain: string | null): void {
  try {
    if (domain) window.localStorage.setItem(SSO_REMEMBERED_DOMAIN_KEY, domain);
    else window.localStorage.removeItem(SSO_REMEMBERED_DOMAIN_KEY);
  } catch {
    /* no storage, no remembered domain */
  }
}

/** Renders a catalogue template whose `@{domain}` is a machine-readable value:
 * SPEC-SSO-7 §5 wraps domains in `<bdi dir="ltr">` so an RTL paragraph does not
 * reorder the address. Falls back to the plain template when the locale does
 * not carry the placeholder. */
function DomainText({ template, domain }: { template: string; domain: string }) {
  const [head, tail] = template.split("@{domain}");
  if (tail === undefined) return <>{formatTemplate(template, { domain })}</>;
  return (
    <>
      {head}
      <bdi dir="ltr">@{domain}</bdi>
      {tail}
    </>
  );
}

export interface LoginSsoPanelProps {
  /** The safe same-origin path the Google method's start URL carries. */
  returnTo: string;
  className?: string;
}

export function LoginSsoPanel({ returnTo, className }: LoginSsoPanelProps) {
  const catalog = useSsoLocale();
  const t = (key: string) => catalog.ui[key] ?? SSO_LOCALES.en.ui[key] ?? key;

  // useLocation, not useSearchParams: the panel is mounted by the router but the
  // 303 from the gateway is a real navigation, and `location.search` is the one
  // source that is correct in both. Same read as pages/system/error.tsx.
  const { search } = useLocation();
  const params = useMemo(() => ssoParamsFrom(search), [search]);

  // Read once: the remembered domain seeds both the checkbox and the field, and
  // re-reading storage during render would fight the user's own clear action.
  const [initial] = useState(() => ({ remembered: readRememberedDomain() }));
  const rememberedAtMount = initial.remembered;

  const [open, setOpen] = useState(
    () => params.state !== null || rememberedAtMount !== null,
  );
  const [revealedByUser, setRevealedByUser] = useState(false);
  const [email, setEmail] = useState(() => {
    // SPEC-SSO-7 §1.2/§1.1.4 conflict for `unavailable`: the table says the
    // field comes back EMPTY (the address the user typed did not work, so
    // re-typing it is the whole point of that state), which beats the
    // remembered-domain pre-fill. Everywhere else the pre-fill wins.
    if (params.state === "unavailable") return "";
    const prefill = params.domain ?? rememberedAtMount;
    return prefill ? `@${prefill}` : "";
  });
  const [remember, setRemember] = useState(() => rememberedAtMount !== null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(
    () => (params.state === "rate_limited" ? SSO_RATE_LIMIT_SECONDS : 0),
  );

  const emailRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const panelId = useId();
  const messageId = useId();

  // SPEC-SSO-7 §6: the reveal moves focus to the email field.
  useEffect(() => {
    if (open && revealedByUser) emailRef.current?.focus();
  }, [open, revealedByUser]);

  // SPEC-SSO-7 §1.2: after a rate-limited 303, Continue is disabled for 60 s
  // with a visible countdown. The timer only exists while it has something to
  // count, so a normal visit never schedules one.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const rateLimited = params.state === "rate_limited" && secondsLeft > 0;
  const invalid = error !== null;

  function handleEmailChange(value: string) {
    setEmail(value);
    if (error) setError(null);
    // The domain is the only thing that is ever stored, so a keystroke can
    // refresh it but can never write the local part.
    if (remember) writeRememberedDomain(domainFromEmail(value));
  }

  function handleRememberChange(checked: boolean) {
    setRemember(checked);
    writeRememberedDomain(checked ? domainFromEmail(email) : null);
  }

  function clearRemembered() {
    setRemember(false);
    writeRememberedDomain(null);
    setEmail("");
    setError(null);
    emailRef.current?.focus();
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    // The valid path is left alone on purpose: the browser must perform the
    // real POST so the gateway's 303 navigates. Only an incomplete address is
    // intercepted, and then the same submit button reports why. `busy` must
    // never disable a named control: the re-render it triggers can land before
    // the browser collects the form data, and a disabled field is not posted.
    if (!isIncompleteWorkEmail(email)) {
      setError(null);
      setBusy(true);
      return;
    }
    event.preventDefault();
    setError(t("login.sso.emailInvalid"));
    emailRef.current?.focus();
  }

  // The state explains itself; only the domain framing inside it depends on
  // `?domain=`. Gating the whole message on a *valid* domain would leave
  // `?sso=required` with a missing or hostile `domain` rendering no explanation
  // at all, which reads as a broken sign-in page rather than a policy.
  const message =
    params.state === "unavailable"
      ? t("login.sso.unavailableMessage")
      : params.state === "rate_limited"
        ? t("login.sso.rateLimitedMessage")
        : params.state === "required"
          ? t("login.sso.requiredMessage")
          : null;

  return (
    <div className={cn("flex w-full flex-col gap-5", className)} dir={catalog.dir}>
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="w-full justify-center gap-2"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((prev) => {
            if (!prev) setRevealedByUser(true);
            return !prev;
          });
        }}
      >
        <Shield size={20} aria-hidden />
        {t("login.sso.button")}
      </Button>

      {open && (
        <div id={panelId} className="auth-block">
          {message && (
            <div
              data-testid="login-sso-message"
              className="mb-4 flex w-full flex-col gap-2 rounded-lg border border-line bg-bg px-4 py-3"
            >
              <p
                id={messageId}
                role="alert"
                className="text-sm text-text"
                data-sso-state={params.state}
              >
                {params.state === "required" && params.domain ? (
                  <DomainText template={message} domain={params.domain} />
                ) : (
                  message
                )}
              </p>
              {rateLimited && (
                // Announced politely, once per second, rather than as an alert:
                // the alert above already said what happened.
                <p className="text-xs text-muted" aria-live="polite" data-testid="login-sso-countdown">
                  {formatTemplate(t("login.sso.rateLimitedCountdown"), { seconds: String(secondsLeft) })}
                </p>
              )}
              {/* Never rendered without a validated ref (CopyableReference
                  renders nothing for null, and REFERENCE_PATTERN has already
                  rejected anything unsafe). */}
              <CopyableReference reference={params.reference} compact />
            </div>
          )}

          <form method="post" action={SSO_START_ACTION} onSubmit={handleSubmit}>
            <input type="hidden" name="return_to" value={returnTo} />
            <input type="hidden" name="ui_locale" value={catalog.locale} />

            <Field
              label={t("login.sso.emailLabel")}
              htmlFor={fieldId}
              error={error ?? undefined}
              required
            >
              <Input
                ref={emailRef}
                id={fieldId}
                name="email"
                type="email"
                // SPEC-SSO-7 §5: the address itself stays LTR even when the
                // panel is mirrored for Arabic.
                dir="ltr"
                autoComplete="username"
                inputMode="email"
                placeholder={t("login.sso.emailPlaceholder")}
                required
                maxLength={320}
                // readOnly, never `disabled`: handleSubmit sets busy while the
                // browser's native POST is still being built, and a disabled
                // control is left out of the form data set (HTML "constructing
                // the entry list"). `disabled` here sent only return_to and
                // ui_locale, so the gateway answered EMAIL_INVALID and every
                // real sign-in landed on ?sso=unavailable (ALL-5748).
                readOnly={busy}
                value={email}
                error={invalid}
                aria-describedby={message ? messageId : undefined}
                onChange={(event) => handleEmailChange(event.target.value)}
              />
            </Field>

            <div className="mt-3 flex w-full flex-col gap-3">
              <Button
                type="submit"
                size="lg"
                className="w-full justify-center"
                loading={busy}
                disabled={busy || rateLimited}
              >
                {busy ? t("login.sso.continueBusy") : t("login.sso.continue")}
              </Button>

              <div className="flex items-center gap-2.5">
                <Checkbox
                  checked={remember}
                  onCheckedChange={handleRememberChange}
                  ariaLabel={t("login.sso.rememberLabel")}
                  disabled={busy}
                />
                <span className="text-xs text-muted">{t("login.sso.rememberLabel")}</span>
              </div>

              {remember && domainFromEmail(email) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                  <span data-testid="login-sso-remembered-hint">
                    <DomainText
                      template={t("login.sso.rememberedHint")}
                      domain={domainFromEmail(email) ?? ""}
                    />
                  </span>
                  {/* Not "forget the organization" copy: this only clears the
                      remembered domain on this device. */}
                  <button
                    type="button"
                    className="font-medium text-primary underline-offset-2 hover:underline"
                    onClick={clearRemembered}
                  >
                    {t("login.sso.clearRemembered")}
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
